import { SelectionModel } from '@angular/cdk/collections';
import { Component, Input, Output, OnChanges, EventEmitter, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { BroadcastUserInterest, OrganizationFilter, FilterData, BroadcastFiltersData, UserSelectorData, DialogData, OrgLabelsFilter } from '@app/models';
import { OrganizationList, SeniorCenter } from '@app/models/organization.model';
import { MemberUser, SelectorUserData, SelfCareGiverData, SelfCareGiverFormData } from '@app/models/user.model';
import { BROADCAST_MESSAGES, SURVEY_MESSAGES } from '@app/shared/data/messages';
import { appValues, BroadcastFilterMethod, NoProgramFilterValue, SelfCaregiverOptions, SNAP_MESSAGES, WELCOME_MESSAGES } from '@app/shared/data/values';
import { tap } from 'rxjs/operators';
import { DialogComponent } from '../dialog/dialog.component';
import { convertNullValueIfZero } from '@app/helpers';
import { CommonService } from '@app/core';
import { GeneralDataResponse } from '@app/models/user.model';
import { UserLabel } from '@app/models/label.model';
import { DemographicFilter, SearchableFilter, ZipcodeFilter } from '@app/models/organization.model';

@Component({
    selector: 'app-broadcast-filters',
    templateUrl: './broadcast-filters.component.html',
    styleUrls: ['./broadcast-filters.component.scss'],
    standalone: false,
    providers: [
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: { dateInput: { month: 'short', year: 'numeric', day: 'numeric' } },
                display: {
                    dateInput: { year: 'numeric', month: 'short', day: 'numeric' },
                    monthYearLabel: { year: 'numeric', month: 'short' },
                    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
                    monthYearA11yLabel: { year: 'numeric', month: 'long' },
                },
            },
        },
    ]
})
export class BroadcastFiltersComponent implements OnInit, OnChanges {
  formatLabel(value: number) {
    if (value >= 1000) {
      return Math.round(value / 1000) + 'k';
    }

    return value.toString();
  }
  @Input('broadcastType') broadcastType: 'sendBroadcast' | 'welcomeMessage' | 'sendSurvey' = appValues.broadcastParams;
  @Input('orgInfo') orgInfo!: OrganizationList;
  @Input('userSelectorData') userSelectorData!: UserSelectorData;
  @Input('discardChangesNote') discardChangesNote!: string;
  @Input('disabled') disabled: boolean = false;
  @Input('hasNoProgramFilter') hasNoProgramFilter: boolean = false;
  @Input('selectedOrgLabels') selectedOrgLabels!: any[];
  /** BH-10704: the message uses @associated_recipients, so highlight "Linked caregivers". */
  @Input('caregiversOnlyRequired') caregiversOnlyRequired: boolean = false;
  /** BH-10704: member_ref -> why that Individual Outreach selection won't receive the broadcast. */
  @Input('leftOutReasons') leftOutReasons: { [memberRef: string]: string } = {};

  selectionData = new SelectionModel<MemberUser | SelectorUserData>(true, []);
  @Output('individualUsersSelector') individualUsersSelector: EventEmitter<any> = new EventEmitter<any>();
  @Output('setUsersSelectorData') setUsersSelectorData: EventEmitter<UserSelectorData> = new EventEmitter<UserSelectorData>();
  @Output() filtersFetched: EventEmitter<void> = new EventEmitter<void>();
  bUserInterests: BroadcastUserInterest[] = [] as BroadcastUserInterest[];
  audiances: OrganizationFilter | null = null;
  orgLabels: OrgLabelsFilter | null = null;
  customFieldFilters: any = { checkAll: false, options: [] };
  showCustomFieldSection: boolean = false;
  snapBenefits: FilterData[] = [
    {
        name: 'SNAP Eligible',
        key: 'snap_eligibility',
        checked: false,
        value: 0
    },
    {
        name: 'SNAP Enrollment',
        key: 'snap_enrollment',
        checked: false,
        value: 0
    },
    {
        name: 'SNAP Interest',
        key: 'snap_interest',
        checked: false,
        value: true
    }
  ];
  careGiverBenefits: FilterData[] = [
    {
        name: 'PH Interested',
        key: 'ph_interested',
        checked: false,
        value: 0
    },
    {
        name: 'PH Consented',
        key: 'ph_consented',
        checked: false,
        value: 0
    },
    {
        name: 'PH Information Provided',
        key: 'ph_info_provided',
        checked: false,
        value: 0
    }
  ];
  
  languageFilters: SearchableFilter = { checkAll: false, options: [], searchTerm: '' };
  genderFilters: DemographicFilter = { checkAll: false, options: [] };
  raceFilters: DemographicFilter = { checkAll: false, options: [] };
  disabilityFilters: DemographicFilter = { checkAll: false, options: [] };
  veteranFilters: DemographicFilter = { checkAll: false, options: [] };
  zipCodeFilters: ZipcodeFilter = { checkAll: false, options: [], searchTerm: '', hasError: false };
  filtersLoaded: boolean = false;

  ageFilters: {
    min: number;
    max: number;
    minValue: number;
    maxValue: number;
    enabled: boolean;
    hasError: boolean;
    hasData: boolean
  } = {
      min: 0,
      max: 0,
      minValue: 0,
      maxValue: 0,
      enabled: false,
      hasError: false,
      hasData:false
    };

  snapMessages = SNAP_MESSAGES;
  welcomeMessages = WELCOME_MESSAGES;
  appValues = appValues;
  broadcastMessages = BROADCAST_MESSAGES;
  broadcastFilterMethod = BroadcastFilterMethod;
  filtersFor: 'mass' | 'individual' = this.broadcastFilterMethod.massBroadcast.method;
  filterNote: string = '';
  filterWarn: string = '';
  selfCareGiver: { name: string; key: string; checked: boolean; value: SelfCareGiverFormData } = {
    name: 'Family Caregiver',
    key: 'self_caregiver',
    checked: false,
    value: 0
  };
  selfCaregiverOptions = SelfCaregiverOptions;
  /** BH-10704: send only to people with at least one linked member. Applies to Mass and Individual. */
  caregiversOnly: boolean = false;
  NoProgramValue = NoProgramFilterValue;
  ageValidationError: string = '';

  today: Date = new Date();

  activationDateFilter: {
    from: Date | null;
    to: Date | null;
  } = {
    from: null,
    to: null
  };

  constructor(
    private readonly _matDialog: MatDialog,
    private readonly _commonService: CommonService,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    this.selectionData = new SelectionModel<MemberUser | SelectorUserData>(true, this.userSelectorData?.confirmedUsers || []);
  }

  ngOnInit(): void {
    this.getOrganizationFilters();
    this.setfilterNote();
    this.setfilterWarn();
  }

// Method to check if some (but not all) values in a custom field are checked
someCustomFieldValuesChecked(customField: any): boolean {
  if (!customField.values || customField.values.length === 0) {
    return false;
  }
  return customField.values.some((v: any) => v.checked) && 
         !customField.values.every((v: any) => v.checked);
}

// Method to set all values in a specific custom field
setCheckAllCustomField(customField: any, checked: boolean): void {
  customField.checkAll = checked;
  if (customField.values && customField.values.length > 0) {
    customField.values.forEach((v: any) => v.checked = checked);
  } else {
    customField.checked = checked;
  }
  this.updateCustomFieldValue(customField);
}

// Handle checkbox change for custom fields
onCustomFieldCheckboxChange(customField: any, clickedValue: any, event: any): void {
  // For single/dropdown type fields, allow only one selection
  if (customField.field_type === 'single' || customField.field_type === 'dropdown') {
    if (event.checked) {
      // Uncheck all other values when this one is checked
      customField.values.forEach((v: any) => {
        if (v.id !== clickedValue.id) {
          v.checked = false;
        }
      });
    }
  }
  
  // Update the field's checked state and checkAll property
  this.updateCustomFieldValue(customField);
}


  getOrganizationFilters(): void {
    let broadcastTypeParam: any = null;
    if (this.broadcastType === 'welcomeMessage') {
      broadcastTypeParam = 'welcome';
    }
    this._commonService.getOrganizationFiltersById(broadcastTypeParam).subscribe(
      (response: any) => {
        const data = response;
        this.initializeFilters(data);
      },
      error => {
        console.error('Error fetching organization filters:', error);
      }
    );
  }

  private initializeFilters(data: any): void {
    if (data.seniorCenters?.length) {
      this.audiances = {
        ...this.orgInfo,
        checkAll: false,
        checked: false,
        seniorCenters: [...this.orgInfo.seniorCenters].map(sc => ({ ...sc, checked: false }))
      };
      
      if (this.hasNoProgramFilter) {
        this.audiances = {
          ...this.audiances, 
          seniorCenters: [
            {...NoProgramFilterValue, checked: false}, 
            ...this.audiances.seniorCenters
          ]
        };
      }
    } else if (this.hasNoProgramFilter) {
      this.audiances = {
        ...this.orgInfo,
        checkAll: false,
        checked: false,
        seniorCenters: [{ ...NoProgramFilterValue, checked: false }]
      };
    }
    if (data.interests?.length) {
      this.bUserInterests = data.interests
        .filter((interest: any) => interest.is_active !== false)
        .map((interest: any) => ({ ...interest, checked: false }));
    }
    if (data.labels?.length) {
      this.orgLabels = {
        ...this.orgInfo,
        checkAll: false,
        checked: false,
        organizationLabels: data.labels.map((label: any) => ({ ...label, checked: false }))
      };
    }
    if (data.languages?.length) {
      this.languageFilters.options = data.languages.map((lang: string) => ({
        name: lang,
        key: lang.toLowerCase().replace(/\s+/g, '_'),
        checked: false
      }));
    }
    if (data.demographics) {
      if (data.demographics.gender?.length) {
        this.genderFilters.options = data.demographics.gender.map((gender: string) => ({
          name: gender,
          key: gender.toLowerCase().replace(/\s+/g, '_'),
          checked: false
        }));
      }
      if (data.demographics.race?.length) {
        this.raceFilters.options = data.demographics.race.map((race: string) => ({
          name: race,
          key: race.toLowerCase().replace(/\s+/g, '_'),
          checked: false
        }));
      }
      if (data.demographics.disabilityStatus?.length) {
        this.disabilityFilters.options = data.demographics.disabilityStatus.map((status: any) => ({
          name: status,
          key: status.toLowerCase(),
          checked: false
        }));
      }
      if (data.demographics.veteranStatus?.length) {
        this.veteranFilters.options = data.demographics.veteranStatus.map((status: any) => ({
          name: status,
          key: status.toLowerCase(),
          checked: false
        }));
      }
      if (data.demographics.zipcode?.length) {
        this.zipCodeFilters.options = data.demographics.zipcode.map((zip: string) => ({
          name: zip,
          key: zip,
          checked: false
        }));
      }
      if (data.demographics.age && data.demographics.age !== null && data.demographics.age.min !== null && data.demographics.age.max !== null) {
        this.ageFilters.min = data.demographics.age.min;
        this.ageFilters.max = data.demographics.age.max;
        this.ageFilters.minValue = data.demographics.age.min;
        this.ageFilters.maxValue = data.demographics.age.max;
        this.ageFilters.hasData = true;
      } else {
        this.ageFilters.hasData = false;
        this.ageFilters.enabled = false;
        this.ageFilters.min = 0;
        this.ageFilters.max = 0;
        this.ageFilters.minValue = 0;
        this.ageFilters.maxValue = 0;
      }
    }
    if (data.customFieldsFilters?.length) {
      const activeFields = data.customFieldsFilters.filter((field: any) => field.status === 'active');
      this.showCustomFieldSection = activeFields.length > 0;

      this.customFieldFilters.options = activeFields.map((field: any) => {
        const option: any = {
          name: field.field_name,
          key: `custom_field_${field.field_id}`,
          field_id: field.field_id,
          field_type: field.field_type,
          checked: false,
          checkAll: false
        };

        // If field has values (multi-type), map them WITH IDs
        if (field.values && Array.isArray(field.values)) {
          option.values = field.values.map((val: any, index: number) => ({
            id: val.value_id,
            value: val.value,
            checked: false
          }));
        }

        return option;
      });
    } else {
      this.showCustomFieldSection = false;
      this.customFieldFilters.options = [];
    }
    this.filtersLoaded = true;
    this.filtersFetched.emit();
  }

updateCustomFieldValue(customField: any): void {
  // Update parent field's checked state based on child values
  if (customField.values && customField.values.length > 0) {
    customField.checked = customField.values.some((v: any) => v.checked);
    
    // Only update checkAll for multi-type fields
    if (customField.field_type === 'multi') {
      customField.checkAll = customField.values.every((v: any) => v.checked);
    }
  }
  this.updateCheckAllDemographic(this.customFieldFilters, 'customField');
}


getFilterValues(): BroadcastFiltersData {
  const individual = (this.filtersFor != this.broadcastFilterMethod.massBroadcast.method);

  if (individual) {
    let individualFilterData: BroadcastFiltersData = {
      audience: this.selectionData?.selected?.map(usr => usr.member_ref), 
      individual: individual, 
      centers: [],
      orgLabels: [],
      ...(this.caregiversOnly && { caregivers_only: true })
    };
    return individualFilterData;
  }
  
  const selectedInterests = this.getSelectedInterests();
  
  let massFilterData: BroadcastFiltersData = {
    ...this.getSelectedAudience(),
    ...this.getSelectedLabels(),
    individual: individual,
    ...(this.caregiversOnly && { caregivers_only: true })
  };
  
  if (selectedInterests.interestIds?.length) {
    massFilterData['interest_ids'] = selectedInterests.interestIds;
    massFilterData['interestNames'] = selectedInterests.interestNames;
  }
  
  /* getting all form values for checked caregiver interest */
  if (this.selfCareGiver.checked) {
    massFilterData['self_caregiver'] = {
      value: (this.selfCareGiver.value === 0) ? null : this.selfCareGiver.value 
    };
  }

  let phInterested: boolean | null = convertNullValueIfZero(this.careGiverBenefits[0].value);
  let phConsent: boolean | null = convertNullValueIfZero(this.careGiverBenefits[1].value);
  let phInformation: boolean | null = convertNullValueIfZero(this.careGiverBenefits[2].value);

  if (this.careGiverBenefits[0].checked) massFilterData['ph_interested'] = { value: phInterested };
  if (this.careGiverBenefits[1].checked) massFilterData['ph_consented'] = { value: phConsent };
  if (this.careGiverBenefits[2].checked) massFilterData['ph_info_provided'] = { value: phInformation };

  /* getting all form values for checked snap interest */
  let snapEligibilityValue: boolean | null = convertNullValueIfZero(this.snapBenefits[0].value);
  let snapEnrollmentValue: boolean | null = convertNullValueIfZero(this.snapBenefits[1].value);
  let snapInterestValue: boolean | null = convertNullValueIfZero(this.snapBenefits[2].value);

  if (this.snapBenefits[0].checked) massFilterData['snap_eligibility'] = { value: snapEligibilityValue };
  if (this.snapBenefits[1].checked) massFilterData['snap_enrollment'] = { value: snapEnrollmentValue };
  if (this.snapBenefits[2].checked) massFilterData['snap_interest'] = { value: snapInterestValue };

  const advanceFilters: any = {};

  const selectedLanguages = this.languageFilters.options.filter(option => option.checked).map(option => option.name);
  if (selectedLanguages.length > 0) {
    advanceFilters['languages'] = selectedLanguages;
  }

  const selectedGenders = this.genderFilters.options.filter(option => option.checked).map(option => option.key);
  if (selectedGenders.length > 0) {
    advanceFilters['gender'] = selectedGenders;
  }

  const selectedRaces = this.raceFilters.options.filter(option => option.checked).map(option => option.name);
  if (selectedRaces.length > 0) {
    advanceFilters['race'] = selectedRaces;
  }

  const selectedDisabilityStatuses = this.disabilityFilters.options.filter(option => option.checked).map(option => option.name);
  if (selectedDisabilityStatuses.length > 0) {
    advanceFilters['disability'] = selectedDisabilityStatuses;
  }
  const selectedVeteranStatuses = this.veteranFilters.options.filter(option => option.checked).map(option => option.name);
  if (selectedVeteranStatuses.length > 0) {
    advanceFilters['veteran'] = selectedVeteranStatuses;
  }
  const selectedZipCodes = this.zipCodeFilters.options.filter(option => option.checked).map(option => option.key);
  if (selectedZipCodes.length > 0) {
    advanceFilters['zipcodes'] = selectedZipCodes;
  }
  if (this.ageFilters.enabled && this.ageFilters.hasData && !this.ageFilters.hasError && !this.ageValidationError) {
    advanceFilters['age'] = {
      min: this.ageFilters.minValue,
      max: this.ageFilters.maxValue
    };
  }
  if (this.activationDateFilter.from || this.activationDateFilter.to) {
    advanceFilters['activationDate'] = {
      from: this.activationDateFilter.from ? this.formatActivationDate(this.activationDateFilter.from) : '',
      to: this.activationDateFilter.to ? this.formatActivationDate(this.activationDateFilter.to) : ''
    };
  }

  // Add advanced filters to payload
  if (Object.keys(advanceFilters).length > 0) {
    massFilterData['advanceFilters'] = advanceFilters;
  }
  
  const fieldIds: number[] = [];
  const valueIds: number[] = [];

  this.customFieldFilters.options.forEach((option: any) => {
    // Check if field has values (multi/dropdown type)
    if (option.values && option.values.length > 0) {
      const selectedValues = option.values.filter((v: any) => v.checked);
      if (selectedValues.length > 0) {
        fieldIds.push(option.field_id);
        selectedValues.forEach((val: any) => {
          valueIds.push(val.id); 
        });
      }
    } else {
      // For single-type fields without predefined values
      if (option.checked) {
        fieldIds.push(option.field_id);
      }
    }
  });

  // Only add customFieldsFilters if there are selected filters
  if (fieldIds.length > 0) {
    massFilterData['customFieldsFilters'] = {
      fieldIds: fieldIds,
      valueIds: valueIds
    };
  }

  return massFilterData;
}
  
  setFilterValues(data:any) {
    this.caregiversOnly = !!data.caregivers_only;
    if (data.individual) {
      this.filtersFor = this.broadcastFilterMethod.individualBroadcast.method;
    } else {
      this.filtersFor = this.broadcastFilterMethod.massBroadcast.method;
      if (this.audiances) {
        this.audiances = {
          ...this.audiances, 
          seniorCenters: this.audiances.seniorCenters.map(sc => {
            return { ...sc, checked: data.audience.includes(sc.id) };
          })
        };
      }

      if (this.orgLabels) {
        this.orgLabels = {
          ...this.orgLabels, 
          organizationLabels: this.orgLabels.organizationLabels.map(sc => {
            return { 
              ...sc, 
              checked: (data?.orgLabels || []).includes(sc.id) 
            };
          })
        };
      }

      this.bUserInterests = this.bUserInterests.map(userIntrst => {
          return { ...userIntrst, checked: !!(data?.interest_ids?.includes(`${userIntrst.id}`)) };
      });

      const convertZeroIfNull = (filterData: { value: boolean | null; } | undefined) => {
        if (!filterData) {
          return 0;
        }
        return filterData.value == null ? 0 : filterData.value;
      };
      this.careGiverBenefits[0].checked = !!data.ph_interested;
      this.careGiverBenefits[0].value = convertZeroIfNull(data.ph_interested);
      this.careGiverBenefits[1].checked = !!data.ph_consented;
      this.careGiverBenefits[1].value = convertZeroIfNull(data.ph_consented);
      this.careGiverBenefits[2].checked = !!data.ph_info_provided;
      this.careGiverBenefits[2].value = convertZeroIfNull(data.ph_info_provided);
      this.snapBenefits[0].checked = !!data.snap_eligibility;
      this.snapBenefits[0].value = convertZeroIfNull(data.snap_eligibility);
      this.snapBenefits[1].checked = !!data.snap_enrollment;
      this.snapBenefits[1].value = convertZeroIfNull(data.snap_enrollment);
      this.snapBenefits[2].checked = !!data.snap_interest;
      this.snapBenefits[2].value = convertZeroIfNull(data.snap_interest);
      this.selfCareGiver.checked = !!data.self_caregiver;
      this.selfCareGiver.value = !!data.self_caregiver ? data.self_caregiver.value || 0 : 0;

      const advanceFilters = data.advanceFilters || {};
      if (advanceFilters.gender) {
        this.genderFilters.options = this.genderFilters.options.map(option => ({
          ...option,
          checked: advanceFilters.gender.includes(option.key)
        }));
        this.updateCheckAllDemographic(this.genderFilters, 'gender');
      }
      if (advanceFilters.disability) {
        this.disabilityFilters.options = this.disabilityFilters.options.map(option => ({
          ...option,
          checked: advanceFilters.disability.includes(option.name)
        }));
        this.updateCheckAllDemographic(this.disabilityFilters, 'disability');
      }
      if (advanceFilters.veteran) {
        this.veteranFilters.options = this.veteranFilters.options.map(option => ({
          ...option,
          checked: advanceFilters.veteran.includes(option.name)
        }));
        this.updateCheckAllDemographic(this.veteranFilters, 'veteran');
      }
      if (advanceFilters.languages) {
        this.languageFilters.options = this.languageFilters.options.map(option => ({
          ...option,
          checked: advanceFilters.languages.includes(option.name)
        }));
        this.updateCheckAllDemographic({
          ...this.languageFilters,
          searchTerm: this.languageFilters.searchTerm
        }, 'language');
      }
      if (advanceFilters.race) {
        this.raceFilters.options = this.raceFilters.options.map(option => ({
          ...option,
          checked: advanceFilters.race.includes(option.name)
        }));
        this.updateCheckAllDemographic(this.raceFilters, 'race');
      }
      if (advanceFilters.zipcodes) {
        this.zipCodeFilters.options = this.zipCodeFilters.options.map(option => ({
          ...option,
          checked: advanceFilters.zipcodes.includes(option.key)
        }));
        this.updateCheckAllDemographic({
          ...this.zipCodeFilters,
          searchTerm: this.zipCodeFilters.searchTerm,
          hasError: this.zipCodeFilters.hasError
        }, 'zipcode');
      }
      if (advanceFilters.customFields) {
        this.customFieldFilters.options = this.customFieldFilters.options.map((option: any) => {
          const savedField = advanceFilters.customFields.find(
            (cf: any) => cf.field_id === option.field_id
          );

          // Initialize checkAll as false by default
          let updatedOption: any = { ...option, checkAll: false };

          if (savedField) {
            updatedOption = { ...updatedOption, checked: true };

            // If field has values, restore their checked state
            if (option.values && savedField.values) {
              updatedOption.values = option.values.map((v: any) => ({
                ...v,
                checked: savedField.values.includes(v.value)
              }));

              // For multi types, compute checkAll based on restored values
              if (option.field_type === 'multi') {
                updatedOption.checkAll = updatedOption.values.every((v: any) => v.checked);
              }
            }
          }

          return updatedOption;
        });

        // Update the "Select All" state for overall group
        this.updateCheckAllDemographic(this.customFieldFilters, 'customField');
      }

      if (data.customFieldsFilters) {
        const { fieldIds, valueIds } = data.customFieldsFilters;

        this.customFieldFilters.options = this.customFieldFilters.options.map((option: any) => {
          // Check if this field is in the selected fieldIds
          const isFieldSelected = fieldIds.includes(option.field_id);

          if (!isFieldSelected) {
            // Field not selected - return with checked false
            const resetOption: any = { ...option, checked: false, checkAll: false };
            if (resetOption.values) {
              resetOption.values = resetOption.values.map((v: any) => ({ ...v, checked: false }));
            }
            return resetOption;
          }

          // Field is selected
          const updatedOption: any = { ...option };

          if (option.values && option.values.length > 0) {
            // Field has predefined values - check which values are selected
            updatedOption.values = option.values.map((v: any) => ({
              ...v,
              checked: valueIds.includes(v.id)
            }));

            // Update checked state based on values
            updatedOption.checked = updatedOption.values.some((v: any) => v.checked);

            // For multi-type fields, update checkAll
            if (option.field_type === 'multi') {
              updatedOption.checkAll = updatedOption.values.every((v: any) => v.checked);
            } else {
              updatedOption.checkAll = false;
            }
          } else {
            // Field has no predefined values (single-type without values)
            updatedOption.checked = true;
            updatedOption.checkAll = false;
          }

          return updatedOption;
        });

        // Update the overall "Select All" state for custom fields
        this.updateCheckAllDemographic(this.customFieldFilters, 'customField');
      } else {
        // No custom field filters in data - reset all
        this.customFieldFilters.options = this.customFieldFilters.options.map((option: any) => {
          const resetOption: any = { ...option, checked: false, checkAll: false };
          if (resetOption.values) {
            resetOption.values = resetOption.values.map((v: any) => ({ ...v, checked: false }));
          }
          return resetOption;
        });
        this.customFieldFilters.checkAll = false;
      }

      if (advanceFilters.age && this.ageFilters.hasData) {
        this.ageFilters.enabled = true;
        this.ageFilters.minValue = advanceFilters.age.min;
        this.ageFilters.maxValue = advanceFilters.age.max;
      } else {
        this.ageFilters.enabled = false;
        this.ageFilters.minValue = this.ageFilters.hasData ? this.ageFilters.min : 0;
        this.ageFilters.maxValue = this.ageFilters.hasData ? this.ageFilters.max : 0;
      }
      if (advanceFilters.activationDate) {
        const parseDate = (dateStr: any) => {
          const date = dateStr ? new Date(dateStr + 'T00:00:00') : null;
          return date && !isNaN(date.getTime()) ? date : null;
        };
        this.activationDateFilter.from = parseDate(advanceFilters.activationDate.from);
        this.activationDateFilter.to = parseDate(advanceFilters.activationDate.to);
      } else {
        this.activationDateFilter = { from: null, to: null };
      }
    }
  }

  setAllCustomFieldValues(customField: any, checked: boolean) {
    if (customField.values && Array.isArray(customField.values)) {
      customField.values.forEach((v: any) => v.checked = checked);
      this.updateCustomFieldValue(customField);
    }
  }

  getSelectedAudience(): { audience: number[]; centers: string[] } {
    if (!this.audiances) return { audience: [], centers: [] };
    const centers: string[] = [];
    const ids: number[] = [];
    this.audiances?.seniorCenters.forEach(sc => {
      if (sc.checked) {
        ids.push(sc.id);
        centers.push(sc.name);
      }
    });
    return { audience: ids, centers: centers };
  }

  getSelectedLabels(): { orgLabels: number[]; labelNames: string[] } {
    if (!this.orgLabels) return { orgLabels: [], labelNames: [] };
    return this.orgLabels.organizationLabels.reduce<{ orgLabels: number[]; labelNames: string[] }>((acc, sc) => {
      if (sc.checked) {
        acc.orgLabels.push(sc.id);
        acc.labelNames.push(sc.name);
      }
      return acc;
    }, { orgLabels: [], labelNames: [] });
  }

  getSelectedInterests(): { interestIds: string[]; interestNames: string[] } {
    return this.bUserInterests.reduce<{ interestIds: string[]; interestNames: string[] }>((acc, buint) => {
      if (buint.checked) {
        acc.interestIds.push(buint.id.toString());
        acc.interestNames.push(buint.name);
      }
      return acc;
    }, { interestIds: [], interestNames: [] });
  }

  switchFilters(value: 'mass' | 'individual', selector: any) {
    if (this.hasMassFilterChanges || this.selectionData?.selected?.length) {
      this.discardChanges().subscribe(result => {
        if (result) {
          // BH-10704: "Linked caregivers" applies to both modes, so it survives the switch
          const caregiversOnly = this.caregiversOnly;
          this.resetAllFilters();
          this.caregiversOnly = caregiversOnly;
          this.filtersFor = value;
        }
      });
    } else {
      this.filtersFor = value;
    }
    
    if (selector) {
      selector.close();
    }
  }

  resetAllFilters() {
    this.caregiversOnly = false;
    if (!this.audiances) return;
    
    this.audiances = {
      ...this.audiances,
      checkAll: false,
      checked: false,
      seniorCenters: this.audiances?.seniorCenters.map(sc => ({ ...sc, checked: false }))
    };

    if (this.orgLabels) {
      this.orgLabels = {
        ...this.orgLabels,
        checkAll: false,
        checked: false,
        organizationLabels: this.orgLabels?.organizationLabels.map(sc => ({ ...sc, checked: false }))
      };
    }

    this.snapBenefits = this.snapBenefits.map(benefit => ({ ...benefit, checked: false, value: benefit.key === 'snap_interest' ? true : 0 }));
    this.bUserInterests = this.bUserInterests.map(bintest => ({ ...bintest, checked: false }));
    this.careGiverBenefits = this.careGiverBenefits.map(cinterest => ({ ...cinterest, checked: false, value: 0 }));
    this.selfCareGiver = { ...this.selfCareGiver, checked: false, value: 0 };
    this.selectionData.clear();

    this.genderFilters = this.resetDemographicFilters(this.genderFilters);
    this.disabilityFilters = this.resetDemographicFilters(this.disabilityFilters);
    this.veteranFilters = this.resetDemographicFilters(this.veteranFilters);
    this.raceFilters = this.resetDemographicFilters(this.raceFilters);
    this.languageFilters = this.resetDemographicFilters(this.languageFilters, { searchTerm: '' });
    this.zipCodeFilters = this.resetDemographicFilters(this.zipCodeFilters, {
      searchTerm: '',
      hasError: false
    });
    this.customFieldFilters = {
      ...this.customFieldFilters,
      checkAll: false,
      options: this.customFieldFilters.options.map((option: any) => {
        const resetOption: any = { ...option, checked: false, checkAll: false };
        if (resetOption.values) {
          resetOption.values = resetOption.values.map((v: any) => ({ ...v, checked: false }));
        }
        return resetOption;
      })
    };
    this.ageFilters = {
      ...this.ageFilters,
      enabled: false,
      minValue: this.ageFilters.hasData ? this.ageFilters.min : 0,
      maxValue: this.ageFilters.hasData ? this.ageFilters.max : 0
    };
    this.activationDateFilter = { from: null, to: null };

    this.setUsersSelectorData.emit();
  }

  private resetDemographicFilters(filter: any, additionalResets?: any) {
    return {
      ...filter,
      checkAll: false,
      options: filter.options.map((option: any) => ({ ...option, checked: false })),
      ...additionalResets
    };
  }

  toggleSelectionData(data: MemberUser | SelectorUserData) {
    this.selectionData.toggle(data);
    this.setUsersSelectorData.emit({
      selectedUsers: this.selectionData.selected, 
      confirmedUsers: this.selectionData.selected
    });
  }

  isSelectedItem(row: MemberUser | SelectorUserData): boolean {
      return !!this.selectionData.selected?.find(usr => (usr.member_ref == row.member_ref));
  }

  setCheckAll(event: boolean) {
    if (!this.audiances) return;
    this.audiances.checkAll = event;
    this.audiances.seniorCenters = this.audiances.seniorCenters.map(c => ({ ...c, checked: event }));
  }

  setCheckAllLabels(event: boolean) {
    if (!this.orgLabels) return;
    this.selectedOrgLabels = [];
    this.orgLabels.checkAll = event;
    this.orgLabels.organizationLabels = this.orgLabels.organizationLabels.map(c => ({ ...c, checked: event }));
  }

  setCheckAllDemographic(filter: { checkAll: boolean; options: any[] }, event: boolean) {
    filter.checkAll = event;
    filter.options = filter.options.map(option => {
      const updatedOption = { ...option, checked: event };

      // Handle nested custom field values
      if (option.values && Array.isArray(option.values)) {
        updatedOption.values = option.values.map((val: any) => ({
          ...val,
          checked: event
        }));
      }
      return updatedOption;
    });
  }

  updateCheckAll() {
    if (!this.audiances) return;
    this.audiances = {
      ...this.audiances,
      checkAll: this.audiances.seniorCenters.every(c => c.checked)
    };
  }

  updateCheckAllLabels() {
    if (!this.orgLabels) return;
    this.selectedOrgLabels = [];
    this.orgLabels = {
      ...this.orgLabels,
      checkAll: this.orgLabels.organizationLabels.every(c => c.checked)
    };
  }

  updateCheckAllDemographic(filter: { checkAll: boolean; options: any[];[key: string]: any }, filterName: string) {
    if (filterName === 'customField') {
      const allChecked = filter.options.every((option: any) => {
        if (option.values && Array.isArray(option.values)) {
          return option.values.every((val: any) => val.checked);
        }
        return option.checked;
      });

      filter.checkAll = allChecked;
      return;
    }

    let updated: any = {
      ...filter,
      checkAll: filter.options.every((c: any) => c.checked)
    };
    if (filterName === 'language') {
      updated = { ...updated, searchTerm: filter.searchTerm || '' };
    }
    if (filterName === 'zipcode') {
      updated = { ...updated, searchTerm: filter.searchTerm || '', hasError: filter.hasError || false };
    }
    const filterMap: { [key: string]: string } = {
      gender: 'genderFilters',
      disability: 'disabilityFilters',
      veteran: 'veteranFilters',
      language: 'languageFilters',
      race: 'raceFilters',
      zipcode: 'zipCodeFilters'
    };
    const prop = filterMap[filterName];
    if (prop && Object.prototype.hasOwnProperty.call(this, prop)) {
      (this as any)[prop] = updated;
    }
  }

  someChecked() {
    if (!this.audiances) return false;
    return this.audiances?.seniorCenters.filter(c => c.checked).length > 0 && !this.audiances?.checkAll;
  }

  someLabelsChecked() {
    if (!this.orgLabels) return false;
    if (this.selectedOrgLabels?.length > 0) {
      this.orgLabels.organizationLabels.forEach(label => {
        label.checked = this.selectedOrgLabels.includes(label.id);
      });
    }
    return this.orgLabels?.organizationLabels.filter(c => c.checked).length > 0 && !this.orgLabels?.checkAll;
  }

  someFilterChecked(filter: { options: { checked: boolean }[], checkAll: boolean }): boolean {
    return filter.options.filter(c => c.checked).length > 0 && !filter.checkAll;
  }

  get filteredLanguages() {
    if (!this.languageFilters.searchTerm) {
      return this.languageFilters.options;
    }
    return this.languageFilters.options.filter(lang => 
      lang.name.toLowerCase().includes(this.languageFilters.searchTerm.toLowerCase())
    );
  }

  get filteredZipCodes() {
    if (!this.zipCodeFilters.searchTerm) {
      return this.zipCodeFilters.options;
    }
    return this.zipCodeFilters.options.filter(zip => 
      zip.key.includes(this.zipCodeFilters.searchTerm)
    );
  }

  onAgeChange(value: number, type: 'min' | 'max'): void {
    this.ageValidationError = '';
    this.ageFilters.hasError = false;
    if (type === 'min') {
      this.ageFilters.minValue = value;
    } else {
      this.ageFilters.maxValue = value;
    }

    if (this.ageFilters.minValue > this.ageFilters.maxValue) {
      this.ageFilters.hasError = true;
      return;
    }
    if (value < this.ageFilters.min || value > this.ageFilters.max) {
      this.ageValidationError = `Please enter an age between ${this.ageFilters.min} and ${this.ageFilters.max}`;
      if (type === 'min') {
        this.ageFilters.minValue = value < this.ageFilters.min ? this.ageFilters.min : this.ageFilters.max;
      } else {
        this.ageFilters.maxValue = value < this.ageFilters.min ? this.ageFilters.min : this.ageFilters.max;
      }
      return;
    }
    this.ageFilters.enabled = true;
  }

  onAgeFilterToggle(enabled: boolean): void {
    this.ageFilters.enabled = enabled;
    if (!enabled) {
      this.ageFilters.minValue = this.ageFilters.hasData ? this.ageFilters.min : 0;
      this.ageFilters.maxValue = this.ageFilters.hasData ? this.ageFilters.max : 0;
      this.ageFilters.hasError = false;
      this.ageValidationError = '';
    }
  }

  isDisabledAge(): boolean {
    return !this.ageFilters.enabled || this.disabled;
  }

  onActivationDateChange(): void {
    // min/max bindings on the datepicker inputs enforce valid ranges natively;
    // this hook exists for any side-effects needed on date change.
  }

  private formatActivationDate(date: Date): string {
    if (!date || isNaN(date.getTime())) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  isDisabledSnap(snapKey: string) {
    return !this.snapBenefits.find(sb => sb.key === snapKey)?.checked;
  }

  isDisabledCaregiverBenefits(caregiverKey: string) {
    return !this.careGiverBenefits.find(cg => cg.key === caregiverKey)?.checked;
  }

  needToolTip(value: string) {
    return value.length > 20 ? false : true;
  }

  discardChanges() {
    let warnData: DialogData = {
      title: 'Warning',
      message: this.discardChangesNote,
      actions: { cancel: 'CANCEL', ok: 'DISCARD' }
    };
    return this._matDialog.open(DialogComponent, { data: warnData, width: '280px', height: 'auto' }).afterClosed();
  }

  get hasFilterOptions() {
    return (this.broadcastType == appValues.broadcastParams) || (this.broadcastType == appValues.surveyParams) || (this.broadcastType == appValues.welcomeMessageParams);
  }

  get hasMassFilterChanges() {
    return (this.filtersFor == this.broadcastFilterMethod.massBroadcast.method) 
      && (this.audiances?.seniorCenters.some(c => c.checked)
        || this.orgLabels?.organizationLabels.some(c => c.checked)
        || this.snapBenefits.some(benefit => benefit.checked)
        || this.bUserInterests.some(intrest => intrest.checked)
        || this.careGiverBenefits.some(intrest => intrest.checked) 
        || this.selfCareGiver.checked
        || this.genderFilters.options.some(option => option.checked)
        || this.disabilityFilters.options.some(option => option.checked)
        || this.veteranFilters.options.some(option => option.checked)
        || this.languageFilters.options.some(option => option.checked)
        || this.raceFilters.options.some(option => option.checked)
        || this.zipCodeFilters.options.some(option => option.checked)
        || this.customFieldFilters.options.some((option : any) => option.checked)
        || this.ageFilters.enabled
        || !!(this.activationDateFilter.from || this.activationDateFilter.to));
  }

  get hasChanges() {
    return this.hasMassFilterChanges 
      || ((this.filtersFor == this.broadcastFilterMethod.individualBroadcast.method) 
        && this.selectionData?.selected?.length)
      || this.caregiversOnly;
  }

  get validFilters() {
    return (
      (this.filtersFor == this.broadcastFilterMethod.massBroadcast.method)
      && this.audiances?.seniorCenters.some(c => c.checked)
      && !this.ageFilters.hasError
      && !this.ageValidationError
    ) || (
        (this.filtersFor == this.broadcastFilterMethod.individualBroadcast.method)
        && !!this.selectionData?.selected?.length
      );
  }

  setfilterNote() {
    switch (this.broadcastType) {
      case appValues.welcomeMessageParams:
        this.filterNote = this.welcomeMessages.FILTER_NOTE;
        break;

      case appValues.broadcastParams:
        this.filterNote = this.snapMessages.SNAP_BENEFITS;
        break;
        
      case appValues.surveyParams:
        this.filterNote = SURVEY_MESSAGES.EXECUTION.FILTER_SEND_SURVEY;
        break;
          
      default:
        this.filterNote = this.snapMessages.SNAP_BENEFITS;
        break;
    }
  }

  setfilterWarn() {
    switch (this.broadcastType) {
      case appValues.welcomeMessageParams:
        this.filterWarn = this.welcomeMessages.FILTER_WARN;
        break;

      case appValues.broadcastParams:
        this.filterWarn = this.snapMessages.FILTER_WARN;
        break;
        
      case appValues.surveyParams:
        this.filterWarn = SURVEY_MESSAGES.EXECUTION.FILTER_WARN;
        break;
          
      default:
        this.filterWarn = this.snapMessages.FILTER_WARN;
        break;
    }
  }

}
